import { Avatar, Box, CircularProgress, TextField, Typography } from "@mui/material"
import { AccountBalanceWalletOutlined, BusinessOutlined, ChevronRightOutlined, PhoneIphoneOutlined } from "@mui/icons-material"
import { LoadingButton } from "@mui/lab"
import { FC, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import PageTitle from "../Common/PageTitle"
import ConfirmPinDialog from "../Common/ConfirmPinDialog"
import { Apis, getImageUrl } from "../../helpers"
import { EventsEmit } from "../../configs/events"
import { Connect, mapDispatchToProps, mapStateToProps } from "../../Redux"
import { PropertyProps } from "../../types/properties"
import SelectBankAccountModal, { BankAccountItem } from "./selectBankAccountModal"

interface Props {
    selectedProperty?: PropertyProps
    setRedux?: (data: any) => void
}
interface StateProps {
    loading: boolean
    property: any
    activeMethod: 'Phone' | 'Bank'
    phone: string
    selectedBankAccount: BankAccountItem | null
    phoneError: string | null
    bankAccountError: string | null
    showBankSelect: boolean
    showPin: boolean
    saving: boolean
    pinError: string | null
}

const METHODS = [
    { label: 'M-Pesa', value: 'Phone' as const, icon: <PhoneIphoneOutlined sx={{ fontSize: 17 }} /> },
    { label: 'Bank Transfer', value: 'Bank' as const, icon: <BusinessOutlined sx={{ fontSize: 17 }} /> },
]

const PaymentAccount: FC<Props> = ({ selectedProperty, setRedux }) => {
    const navigate = useNavigate()
    const [state, setState] = useState<StateProps>({
        loading: true,
        property: null,
        activeMethod: 'Phone',
        phone: '',
        selectedBankAccount: null,
        phoneError: null,
        bankAccountError: null,
        showBankSelect: false,
        showPin: false,
        saving: false,
        pinError: null
    })
    const {
        loading, property, activeMethod, phone, selectedBankAccount,
        phoneError, bankAccountError, showBankSelect, showPin, saving, pinError
    } = state

    useEffect(() => {
        if (!selectedProperty?._id) { navigate('/properties'); return }
        Apis({ module: 'Properties', action: 'getPropertiesInfo', searchVal: selectedProperty.name, page: 0, pageSize: 10 })
            .then((resp: any) => {
                const found = (resp?.properties || []).find((p: any) => p._id === selectedProperty._id)
                const paymentAccount = found?.paymentAccount
                setState(s => ({
                    ...s,
                    loading: false,
                    property: found || selectedProperty,
                    activeMethod: paymentAccount?.active || 'Phone',
                    phone: paymentAccount?.Phone ? paymentAccount.Phone.replace(/^\+254/, '0') : '',
                    selectedBankAccount: paymentAccount?.Bank?.bankId ? paymentAccount.Bank : null
                }))
            })
            .catch(() => setState(s => ({ ...s, loading: false, property: selectedProperty })))
        // eslint-disable-next-line
    }, [])

    const submit = () => {
        let hasError = false
        const digits = phone.replace(/\D/g, '')
        if (activeMethod === 'Phone' && digits.length < 9) {
            setState(s => ({ ...s, phoneError: 'Please enter a valid phone number.' }))
            hasError = true
        }
        if (activeMethod === 'Bank' && !selectedBankAccount) {
            setState(s => ({ ...s, bankAccountError: 'Please select a bank account.' }))
            hasError = true
        }
        if (hasError) return
        setState(s => ({ ...s, showPin: true }))
    }

    const save = (pin: string) => {
        const planName = property?.plan?.name || (typeof property?.plan === 'string' ? property.plan : undefined)
        const newPayment: any = { active: activeMethod }
        if (activeMethod === 'Phone') {
            const digits = phone.replace(/\D/g, '').replace(/^0/, '')
            newPayment.Phone = '+254' + digits
        } else {
            newPayment.Bank = selectedBankAccount
        }
        setState(s => ({ ...s, saving: true, pinError: null }))
        Apis({
            module: 'Properties',
            action: 'updatePaymentMethod',
            _id: property._id,
            paymentAccount: { ...property?.paymentAccount, ...newPayment },
            plan: planName,
            pin
        }).then((resp: any) => {
            if (resp?.error) {
                setState(s => ({ ...s, saving: false, pinError: resp.message || 'Something went wrong.' }))
                return
            }
            EventsEmit('app', { info: { message: resp?.message || 'Payment settings updated.', status: 'success' } })
            if (setRedux && selectedProperty)
                setRedux({ selectedProperty: { ...selectedProperty, hasPaymentAccount: true } })
            navigate('/dashboard')
        }).catch(() => setState(s => ({ ...s, saving: false, pinError: 'Something went wrong. Please try again.' })))
    }

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
    }

    return (
        <Box sx={{ pr: 5, maxWidth: 520 }}>
            <PageTitle title="Payment Account" />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 3, mb: 3 }}>
                <Avatar src={getImageUrl(selectedProperty?.logos, 'small')} variant="rounded" sx={{ width: 40, height: 40 }} />
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{selectedProperty?.name}</Typography>
            </Box>

            <Box sx={{ bgcolor: '#fff', borderRadius: 3, p: 2.5, boxShadow: '0 1px 4px rgba(0,0,0,.06), 0 4px 16px rgba(0,0,0,.04)' }}>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2.5 }}>
                    Choose how you'd like to receive rent payments for this property.
                </Typography>

                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', mb: 1 }}>Payment Method</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                    {METHODS.map(({ label, value, icon }) => {
                        const active = activeMethod === value
                        return (
                            <Box
                                key={value}
                                onClick={() => setState(s => ({ ...s, activeMethod: value, phoneError: null, bankAccountError: null }))}
                                sx={{
                                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8,
                                    py: 1.2, borderRadius: 2, cursor: 'pointer', border: '1.5px solid',
                                    borderColor: active ? '#8c7c0c' : '#e2e8f0',
                                    bgcolor: active ? 'rgba(140,124,12,.06)' : '#fff',
                                    color: active ? '#8c7c0c' : '#64748b'
                                }}
                            >
                                {icon}
                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{label}</Typography>
                            </Box>
                        )
                    })}
                </Box>

                {activeMethod === 'Phone' ? (
                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', mb: 1 }}>M-Pesa Phone Number</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="0712345678"
                            value={phone}
                            onChange={e => setState(s => ({ ...s, phone: e.target.value, phoneError: null }))}
                            error={Boolean(phoneError)}
                            helperText={phoneError}
                            InputProps={{ startAdornment: <Typography sx={{ fontSize: 13, color: 'text.secondary', mr: 0.5 }}>🇰🇪 +254</Typography> }}
                        />
                    </Box>
                ) : (
                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', mb: 1 }}>Bank Account</Typography>
                        <Box
                            onClick={() => setState(s => ({ ...s, showBankSelect: true }))}
                            sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1.5,
                                borderRadius: 2, cursor: 'pointer', border: '1px solid', borderColor: bankAccountError ? '#c62828' : '#e2e8f0'
                            }}
                        >
                            {selectedBankAccount ? (
                                <Box>
                                    <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{selectedBankAccount.bank_name}</Typography>
                                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{selectedBankAccount.accountNumber}</Typography>
                                </Box>
                            ) : (
                                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Select bank account</Typography>
                            )}
                            <ChevronRightOutlined sx={{ fontSize: 18, color: 'text.disabled' }} />
                        </Box>
                        {bankAccountError && (
                            <Typography sx={{ fontSize: 12, color: '#c62828', mt: 0.5 }}>{bankAccountError}</Typography>
                        )}
                    </Box>
                )}

                <LoadingButton
                    fullWidth
                    variant="contained"
                    onClick={submit}
                    startIcon={<AccountBalanceWalletOutlined />}
                    sx={{ textTransform: 'none', borderRadius: 2, py: 1.4, mt: 3.5 }}
                >
                    Save
                </LoadingButton>
            </Box>

            {showBankSelect && (
                <SelectBankAccountModal
                    selectedAccountId={selectedBankAccount?._id}
                    close={() => setState(s => ({ ...s, showBankSelect: false }))}
                    onSelect={(account) => setState(s => ({ ...s, selectedBankAccount: account, bankAccountError: null, showBankSelect: false }))}
                />
            )}

            {showPin && (
                <ConfirmPinDialog
                    title="Enter PIN"
                    message="Confirm your PIN to update payment settings."
                    processing={saving}
                    errorMessage={pinError}
                    onConfirm={save}
                    close={() => setState(s => ({ ...s, showPin: false, pinError: null }))}
                />
            )}
        </Box>
    )
}

export default Connect(mapStateToProps, mapDispatchToProps)(PaymentAccount)
